import {Card, DataStatus} from 'platform/components';
import {Grid, Hide, Show, Space} from 'platform/foundation';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {NewOfferPurchaseBrokerageVehicleResponseBody} from '@dms/api/finance';
import {useGetPurchaseBusinessCasePricesOverviewQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {usePermissions, usePricePermissions} from '@dms/shared';
import {PriceBox} from '@dms/teas';

type BrokerageFeesSummaryProps = {
  purchaseVehicle: NewOfferPurchaseBrokerageVehicleResponseBody;
  businessCaseId: string;
};

const BrokerageFeesSummary = (props: BrokerageFeesSummaryProps) => {
  const {
    data: purchaseVehiclePrices,
    isLoading,
    isError,
  } = useGetPurchaseBusinessCasePricesOverviewQuery({businessCaseId: props.businessCaseId});

  const {
    canReadVehiclePurchasePrice,
    canReadVehicleSellingPrice,
    canReadVehicleEarnings,
    canReadBusinessCaseBrokerageFees,
  } = usePricePermissions({
    businessCaseRecordId: props.businessCaseId,
    vehicleRecordId: props.purchaseVehicle.vehicleId,
  });

  const {data: vehicleParticipation} = useGetParticipationQuery({
    recordId: props.purchaseVehicle.vehicleId,
    resourceId: EntityResourceIds.vehicle,
  });

  const [canReadCosts] = usePermissions({
    permissionKeys: ['removeVehiclePhotoBackground'],
    scopes: {
      removeVehiclePhotoBackground: {
        participation: vehicleParticipation,
        branchId: props.purchaseVehicle.vehicleId,
      },
    },
  });

  const {purchaseVehicle} = props;

  return (
    <Card data-testid={testIds.businessCase.buying('summary')} variant="inlineGrey">
      <DataStatus
        isEmpty={isNilOrEmpty(purchaseVehiclePrices)}
        isLoading={isLoading}
        isError={isError}
        minHeight={10}
      >
        <Grid columns={[1, 2, 4]}>
          <Show when={canReadVehicleSellingPrice}>
            <PriceBox
              data-testid={testIds.businessCase.buying('summary-vehicleSellingPrice')}
              align="left"
              price={purchaseVehiclePrices?.vehicleSellingPrice?.withVat}
              priceSub={purchaseVehiclePrices?.vehicleSellingPrice?.withoutVat}
              isVatDeductible={purchaseVehicle?.pricing?.vatDeductible}
              title={i18n.t('entity.businessCase.labels.vehicleSellingPrice')}
            />
          </Show>
          <Show when={canReadCosts && canReadVehiclePurchasePrice}>
            <PriceBox
              data-testid={testIds.businessCase.buying('summary-totalExpectedCosts')}
              align="left"
              price={purchaseVehiclePrices?.totalCosts}
              title={i18n.t('entity.businessCase.labels.totalExpectedCosts')}
            />
          </Show>
          <Hide when={canReadCosts && canReadVehiclePurchasePrice}>
            <Space fillAvailable />
          </Hide>
          <Show
            when={
              canReadVehicleSellingPrice &&
              canReadVehiclePurchasePrice &&
              canReadVehicleEarnings &&
              canReadBusinessCaseBrokerageFees
            }
          >
            <PriceBox
              data-testid={testIds.businessCase.buying('summary-vehicleGrossMargin')}
              colored
              align="left"
              percentTag={Number(purchaseVehiclePrices?.vehicleMargin?.percentage)}
              price={purchaseVehiclePrices?.vehicleMargin?.amount}
              title={i18n.t('entity.businessCase.labels.vehicleGrossMargin')}
              tooltipText={i18n.t('entity.businessCase.labels.vehicleGrossMarginTooltip')}
            />
            <PriceBox
              data-testid={testIds.businessCase.buying('summary-totalGrossMargin')}
              colored
              align="left"
              percentTag={Number(purchaseVehiclePrices?.totalMargin?.percentage)}
              price={purchaseVehiclePrices?.totalMargin?.amount}
              title={i18n.t('entity.businessCase.labels.totalGrossMargin')}
              tooltipText={i18n.t('entity.businessCase.labels.totalGrossMarginTooltip')}
            />
          </Show>
        </Grid>
      </DataStatus>
    </Card>
  );
};

export const BusinessCasePurchaseVehicleBrokerageFeesSummary = BrokerageFeesSummary;
